import { IAccount, IOperationResult } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { computed, makeObservable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { IEventLoggingComponentProps, withEventLogging } from '../../../../models/Logging';
import { isValidEmail } from '../../../../models/UiUtils';
import { AccountUserViewModel } from '../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../web/components/Button';
import { Checkbox } from '../../../../web/components/Checkbox';
import { Modal } from '../../../../web/components/Modal';
import { TextInput } from '../../../../web/components/TextInput';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../web/components-v2/Select';
import { styleSheet } from './styles';

interface IProps extends IUserSessionComponentProps, IEventLoggingComponentProps, IErrorMessageComponentProps {
	className?: string;
	onRequestClose?(result?: unknown, canceled?: boolean): void;
	isOpen: boolean;
	styles?: StyleDeclarationValue[];
	userVM: AccountUserViewModel;
	account: IAccount;
}

interface IState {
	sendFromEmail: string;
	emailAddresses: string[];
	isGoogle: boolean;
}

class _SharedMailboxModal extends React.Component<IProps, IState> {
	public constructor(props: IProps) {
		super(props);
		makeObservable<_SharedMailboxModal, 'accountUserViewModel' | 'shouldShowDropdown'>(this, {
			accountUserViewModel: computed,
			shouldShowDropdown: computed,
		});
		this.state = {
			sendFromEmail: this.props.userVM.preferences.sendFromEmailAddress?.emailAddress,
			emailAddresses: [],
			isGoogle: this.props.userVM.emailProvider === 'Gmail' || this.props.userVM.emailProvider === 'Google',
		};
	}

	public componentDidMount() {
		if (this.state.isGoogle) {
			this.fetchEmailAddresses(this.props.userVM);
		}
	}

	public componentDidUpdate(prevProps: IProps) {
		if (this.props.userVM !== prevProps.userVM) {
			if (this.state.isGoogle) {
				this.fetchEmailAddresses(this.props.userVM);
			}
		}
	}

	public render() {
		const { className, onRequestClose, styles, userVM } = this.props;
		const { emailAddresses } = this.state;
		return (
			<Modal
				className={`${css(styleSheet.container, ...(styles || []))} component-class ${className || ''}`}
				isOpen={!!this.props.isOpen}
				onRequestClose={onRequestClose}
				useStyledHeader={true}
			>
				<div className={css(styleSheet.header)}>
					{this.state.isGoogle ? 'Select Send As Email Address' : 'Setup Shared Mailbox'}
				</div>
				<div className={css(styleSheet.modalBody)}>
					{(this.shouldShowDropdown && (
						<div className={css(styleSheet.inputRow)}>
							<Select
								value={this.state.sendFromEmail ?? ''}
								onValueChange={value => this.setState({ sendFromEmail: value || null })}
							>
								<SelectTrigger styles={styleSheet.useSharedMailbox}>
									<span>{this.state.sendFromEmail ?? 'Select Email Address'}</span>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value=''>Select Email Address</SelectItem>
									{emailAddresses.map(em => (
										<SelectItem key={em} value={em}>
											{em}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					)) || (
						<>
							<div className={css(styleSheet.inputStacked)}>
								<div className={css(styleSheet.info)}>Send from Email Address</div>
								<TextInput
									autoComplete='off'
									className={css(styleSheet.textBox)}
									disabled={this.accountUserViewModel.isBusy}
									value={this.state.sendFromEmail}
									inputId='email'
									onChange={this.onChangeEmail}
									placeholder='Enter the shared mailbox address'
									type='text'
								/>
							</div>
							<div className={css(styleSheet.inputRow)}>
								<Checkbox
									childrenPosition='right'
									className={css(styleSheet.useSharedMailbox)}
									checked={userVM.preferences.useSharedMailbox}
									onChange={() => (userVM.preferences.useSharedMailbox = !userVM.preferences.useSharedMailbox)}
								>
									<span className={css(styleSheet.useSharedMailboxLabel)}>Use Shared Mailbox</span>
								</Checkbox>
							</div>
						</>
					)}
					<div className={css(styleSheet.inputRow)}>
						<Button
							styles={[styleSheet.modalButtons, styleSheet.saveButton]}
							kind='primary'
							onClick={this.onChange}
							disabled={this.accountUserViewModel.isBusy}
						>
							Save
						</Button>
						<Button
							styles={[styleSheet.modalButtons]}
							kind='reverse'
							onClick={this.props.onRequestClose}
							disabled={this.accountUserViewModel.isBusy}
						>
							Cancel
						</Button>
						{(this.state.sendFromEmail && (
							<Button
								styles={[styleSheet.modalButtons]}
								kind='destructive'
								onClick={this.onReset}
								disabled={this.accountUserViewModel.isBusy}
							>
								Reset
							</Button>
						)) ||
							null}
					</div>
				</div>
			</Modal>
		);
	}

	private get accountUserViewModel() {
		const { userSession, userVM } = this.props;
		// creating a new view model because impersonating pollutes the original view model
		// must be computed because userVM will be null in the constructor
		return new AccountUserViewModel(userSession, userVM);
	}

	private get shouldShowDropdown() {
		if (this.accountUserViewModel.toJs() !== null) {
			return this.state.isGoogle;
		}
		return false;
	}

	private onChange = () => {
		const { sendFromEmail } = this.state;
		const { logApiError, logEvent, errorMessages, onRequestClose, userVM } = this.props;

		logEvent('ChangingEmail');
		if (!isValidEmail(sendFromEmail)) {
			toast.error('Please include a valid email');
			return;
		}
		this.accountUserViewModel.impersonate({
			account: { id: this.accountUserViewModel.accountId },
			user: { id: this.accountUserViewModel.id },
		});
		this.accountUserViewModel
			.changeSendFromEmailAddress(sendFromEmail)
			.then(() => {
				if (userVM.preferences.useSharedMailbox !== null) {
					return this.accountUserViewModel.changeUseSharedMailbox(userVM.preferences.useSharedMailbox);
				}
			})
			.then(() => {
				toast.success('Successfully updated send as email address');

				onRequestClose();
			})
			.catch(error => {
				errorMessages.pushApiError(error);

				logApiError('ChangingEmail-Error', error);
			});
	};

	private onReset = () => {
		const { logApiError, logEvent, errorMessages, onRequestClose, userVM } = this.props;

		logEvent('ChangingEmail');
		this.accountUserViewModel.impersonate({
			account: { id: this.accountUserViewModel.accountId },
			user: { id: this.accountUserViewModel.id },
		});
		this.accountUserViewModel
			.deleteSendFromEmailAddress()
			.then(() => {
				if (userVM.preferences.useSharedMailbox !== null) {
					return this.accountUserViewModel.changeUseSharedMailbox(userVM.preferences.useSharedMailbox);
				}
			})
			.then(() => {
				toast.success('Successfully updated send as email address');

				onRequestClose();
			})
			.catch(error => {
				errorMessages.pushApiError(error);

				logApiError('ChangingEmail-Error', error);
			});
	};

	private fetchEmailAddresses = (user: AccountUserViewModel) => {
		if (user !== null && user?.accountId !== null) {
			this.accountUserViewModel.impersonate({
				account: { id: this.accountUserViewModel.accountId },
				user: { id: this.accountUserViewModel.id },
			});
			this.accountUserViewModel.getAliases().then((result: IOperationResult<string[]>) => {
				this.setState({ emailAddresses: result.value });
			});
		}
	};

	private onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) =>
		this.setState({ sendFromEmail: event.target.value });
}

const SharedMailboxModalAsObserver = observer(_SharedMailboxModal);
const SharedMailboxModalWithContext = inject(
	UserSessionViewModelKey,
	ErrorMessagesViewModelKey
)(SharedMailboxModalAsObserver);
export const SharedMailboxModal = withEventLogging(SharedMailboxModalWithContext, 'SharedMailboxModal');
